import "@src/components/surveys/surveys.css";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, useMutation, useQuery } from "@apollo/client";
import dayjs from "dayjs";
import { BACKEND_URL } from "@src/config/apollo";
import { getMySurveyQuery } from "@src/util/query/survey.query";
import { submitMySurveyMutation } from "@src/util/mutation/survey.mutation";
import { getAppraisalQuery } from "@src/util/query/appraisal.query";
import { signOffAppraisalMutation, submitAppraisalSelfAssessmentMutation } from "@src/util/mutation/appraisal.mutation";
import { genericFileUploadSignatureMutation } from "@src/util/mutation/genericFileUpload.mutation";
import { getComplianceDocumentFormQuery } from "@src/util/query/compliance.query";
import { acknowledgeComplianceDocumentMutation, markComplianceDocumentViewedMutation } from "@src/util/mutation/compliance.mutation";
import { FormOnPdf, SurveyFormFill } from "@src/components/formOnPdf/formOnPdf";
import { SignHere } from "@src/components/surveys/surveyAnswerForm";
import { isAnswered } from "@src/components/appraisals/appraisalHelpers";

/**
 * /embed/survey/:responseId, /embed/appraisal/:id and /embed/document/:shareId - what the app
 * (Workforce360) shows in a web view for a survey or appraisal on the company's own form, or a
 * compliance document with fields, so it's filled in and signed on the form itself (with pinch-zoom). The app injects its login token as window.__WFC_APP_TOKEN (a
 * "#t=" fragment works too, for testing) and listens for { type: "done" } to close.
 */

const tokenOf = () => window.__WFC_APP_TOKEN || new URLSearchParams(window.location.hash.slice(1)).get("t") || "";
// which server to talk to: live, always the live one; in development the link says (the phone
// reaches the server by this machine's network address, not "localhost")
const apiOf = () => {
    const given = process.env.NODE_ENV !== "production" ? new URLSearchParams(window.location.hash.slice(1)).get("api") : null;
    return given && /^http:\/\/[\d.]+:\d+\/graphql$/.test(given) ? given : `${BACKEND_URL}/graphql`;
};
const tell = (message) => { try { window.ReactNativeWebView?.postMessage(JSON.stringify(message)); } catch { /* not in the app */ } };

const Shell = ({ title, children }) => {
    const token = tokenOf();
    const client = useMemo(() => new ApolloClient({
        link: new HttpLink({ uri: apiOf(), headers: { authorization: `Bearer ${token}` } }),
        cache: new InMemoryCache(),
    }), [token]);
    return (
        <ApolloProvider client={client}>
            <div className="svy-page embed">
                <div className="svy-card wide">
                    {title && <div className="svy-card-head"><div className="svy-title">{title}</div></div>}
                    <div className="svy-card-body">{token ? children : <State title="Please open this from the app" />}</div>
                </div>
            </div>
        </ApolloProvider>
    );
};

const State = ({ title, text = "" }) => (
    <div className="svy-state"><div className="svy-state-title">{title}</div><div className="svy-state-text">{text}</div></div>
);

// ------------------------------------------------------------------ survey

const SurveyBody = () => {
    const { responseId } = useParams();
    const { data, loading, error } = useQuery(getMySurveyQuery, { variables: { responseId }, fetchPolicy: "network-only" });
    const [submit, { loading: submitting }] = useMutation(submitMySurveyMutation);
    const [done, setDone] = useState(false);
    const [failure, setFailure] = useState(null);
    const form = data?.getMySurvey?.response;
    if (loading) return <State title="Opening..." />;
    if (error || !form) return <State title="This survey couldn't be opened" text={error?.message || ""} />;
    if (done || form.state === "submitted") return <State title="Thank you" text={done ? "Your answers have been sent." : "You've already answered this survey."} />;
    if (form.state === "closed") return <State title="This survey has closed" text="It's no longer taking answers." />;
    const send = async (answers, signed) => {
        setFailure(null);
        try {
            await submit({ variables: { responseId, answers, ...(signed || {}) } });
            setDone(true);
            tell({ type: "done", message: "Your answers have been sent." });
        } catch (err) { setFailure(err.message); }
    };
    return (
        <>
            <div className="svy-hint" style={{ marginBottom: 12 }}>{form.title} · please answer by {dayjs(form.closesOn).format("D MMMM YYYY")}. Fill in the highlighted boxes (Start takes you through them), read to the end, then send.</div>
            {form.anonymous && <div className="svy-note">Your answers are anonymous - nobody can see which answers are yours.</div>}
            {failure && <div className="svy-note" style={{ background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }}>{failure}</div>}
            <SurveyFormFill form={form} submitting={submitting} onSubmit={send} />
        </>
    );
};

export const EmbedSurveyPage = () => <Shell><SurveyBody /></Shell>;

// ------------------------------------------------------------------ appraisal (the employee's own)

const AppraisalBody = () => {
    const { id } = useParams();
    const { data, loading, error } = useQuery(getAppraisalQuery, { variables: { id }, fetchPolicy: "network-only" });
    const [submitSelf, { loading: sending }] = useMutation(submitAppraisalSelfAssessmentMutation);
    const [signOff, { loading: signing }] = useMutation(signOffAppraisalMutation);
    const [uploadUrl] = useMutation(genericFileUploadSignatureMutation);
    const [answers, setAnswers] = useState({});
    const [readToEnd, setReadToEnd] = useState(false);
    const [agree, setAgree] = useState("agree");
    const [comment, setComment] = useState("");
    const [signed, setSigned] = useState(null);
    const [done, setDone] = useState(null);
    const [failure, setFailure] = useState(null);
    const a = data?.getAppraisal?.response;
    if (loading) return <State title="Opening..." />;
    if (error || !a) return <State title="This appraisal couldn't be opened" text={error?.message || ""} />;
    if (done) return <State title="Thank you" text={done} />;
    const viewer = a.viewer || {};
    const mine = (a.template?.sections || []).flatMap((s) => s.questions).filter((q) => q.answeredBy === "officer" || q.answeredBy === "both");
    const missing = mine.filter((q) => q.required !== false && !isAnswered(q, answers[q.id]));
    const finish = (message) => { setDone(message); tell({ type: "done", message }); };

    const sendSelf = async () => {
        setFailure(null);
        try {
            await submitSelf({ variables: { input: { appraisalId: a._id, answers } } });
            finish("Your self-assessment has been sent to your reviewer.");
        } catch (err) { setFailure(err.message); }
    };
    // a drawn signature goes up to storage first (in the employee's own folder), then the sign-off
    const sign = async () => {
        setFailure(null);
        try {
            let signatureKey = null;
            if (signed?.signature) {
                const fileName = `employee/${a.employeeId}/${Date.now()}appraisalsign.png`;
                const res = await uploadUrl({ variables: { input: { fileName, objectType: "employee" } } });
                const link = res?.data?.genericFileUploadSignatureUrl?.response;
                const blob = await (await fetch(signed.signature)).blob();
                const put = await fetch(link.url, { method: "PUT", body: blob, headers: { "Content-Type": "image/png" } });
                if (!put.ok) throw new Error("Your signature didn't upload. Please try again.");
                signatureKey = link.originalFileName || fileName;
            }
            await signOff({ variables: { input: { appraisalId: a._id, response: agree, comment, ...(signatureKey ? { signatureKey } : { typedName: signed.typedName }) } } });
            finish("Signed - thank you. A copy is in your documents.");
        } catch (err) { setFailure(err.message); }
    };

    const officerShown = a.selfAssessment ? a.officerAnswers || {} : {};
    const stampValues = { ...(a.stampValues || {}), ...(viewer.canSign && signed ? { officerSignature: signed.signature ? { url: signed.signature } : { text: signed.typedName } } : {}) };
    return (
        <>
            <div className="svy-hint" style={{ marginBottom: 12 }}>
                {viewer.canSelfAssess ? "Your self-assessment: answer each highlighted box (Start takes you through them), read to the end, then send it to your reviewer."
                    : viewer.canSign ? `${a.reviewerName || "Your reviewer"} has completed your appraisal. Read it to the end, then sign it below.`
                        : `${a.title}`}
            </div>
            {failure && <div className="svy-note" style={{ background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }}>{failure}</div>}
            <FormOnPdf pdfUrl={a.formPdfUrl} template={a.template} kind="appraisal" mode={viewer.canSelfAssess ? "fill" : "view"} side="officer"
                answers={answers} onAnswer={(qid, patch) => setAnswers((prev) => ({ ...prev, [qid]: { ...(prev[qid] || {}), ...patch } }))}
                shown={{ officer: officerShown, reviewer: a.reviewerAnswers || {} }} stampValues={stampValues} onReadToEnd={() => setReadToEnd(true)}
                renderBar={({ answered, total, left, next, nextLabel }) => (
                    <div className="fop-bar">
                        <span className="fop-bar-count">{answered} of {total} answered</span>
                        {left > 0 && <button type="button" className="svy-clear" data-next-field onClick={next}>{nextLabel}</button>}
                        <button type="button" className="svy-submit" disabled={sending || missing.length > 0 || !readToEnd} onClick={sendSelf}>
                            {sending ? "Sending..." : missing.length ? `${missing.length} to answer` : !readToEnd ? "Scroll to the end of the form" : "Send to my reviewer"}
                        </button>
                    </div>
                )} />
            {viewer.canSign && (
                <div className="svy-form" style={{ marginTop: 16 }}>
                    <div className="svy-question-text">Do you agree with your appraisal?</div>
                    <div className="svy-options" style={{ marginBottom: 12 }}>
                        {[["agree", "I agree"], ["disagree", "I don't agree"]].map(([value, label]) => (
                            <button key={value} type="button" className={`svy-option${agree === value ? " selected" : ""}`} onClick={() => setAgree(value)}>{label}</button>
                        ))}
                    </div>
                    <textarea className="svy-textarea" value={comment} placeholder={agree === "disagree" ? "Say why (kept with your appraisal)" : "Comments (optional)"} onChange={(e) => setComment(e.target.value)} />
                    <SignHere onChange={setSigned} />
                    <div className="svy-submit-row">
                        <button type="button" className="svy-submit" disabled={signing || !signed || !readToEnd} onClick={sign}>
                            {signing ? "Signing..." : !readToEnd ? "Read the form to the end" : !signed ? "Sign above" : "Sign my appraisal"}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export const EmbedAppraisalPage = () => <Shell><AppraisalBody /></Shell>;

// ------------------------------------------------------------------ a compliance document with fields

const DocumentBody = () => {
    const { shareId } = useParams();
    const { data, loading, error } = useQuery(getComplianceDocumentFormQuery, { variables: { shareId }, fetchPolicy: "network-only" });
    const [markViewed] = useMutation(markComplianceDocumentViewedMutation);
    const [acknowledge, { loading: sending }] = useMutation(acknowledgeComplianceDocumentMutation);
    const [uploadUrl] = useMutation(genericFileUploadSignatureMutation);
    const [answers, setAnswers] = useState({});
    const [signature, setSignature] = useState(null); // drawn - in the signature box, or below the document
    const [confirmed, setConfirmed] = useState(false); // tick-box documents: tick + typed name
    const [typedName, setTypedName] = useState("");
    const [readToEnd, setReadToEnd] = useState(false);
    const [done, setDone] = useState(null);
    const [failure, setFailure] = useState(null);
    const doc = data?.getComplianceDocumentForm?.response;
    // when they first opened it - part of the audit trail
    useEffect(() => {
        if (doc && doc.status !== "acknowledged") markViewed({ variables: { shareId } }).catch(() => null);
    }, [doc?.shareId]);
    if (loading) return <State title="Opening..." />;
    if (error || !doc) return <State title="This document couldn't be opened" text={error?.message || ""} />;
    if (done) return <State title="Thank you" text={done} />;
    if (doc.status === "acknowledged") return <State title="Already done" text={`You acknowledged this document on ${dayjs(doc.acknowledgedAt).format("D MMMM YYYY")}.`} />;
    if (doc.revoked || doc.documentLive === false) return <State title="This document doesn't need you any more" text="It has been replaced or withdrawn." />;

    const tick = doc.method === "tick";
    const questions = (doc.form?.sections || []).flatMap((s) => s.questions);
    const hasSignBox = (doc.form?.stamps || []).some((s) => s.source === "employeeSignature");
    const missing = questions.filter((q) => q.required !== false && !isAnswered(q, answers[q.id]));
    const blocker = missing.length ? `${missing.length} to fill in`
        : !tick && !signature ? (hasSignBox ? "Sign in the signature box" : "Sign below the document")
            : !readToEnd ? "Scroll to the end of the document"
                : tick && !(confirmed && typedName.trim().length >= 2) ? "Tick and type your name below" : null;

    const send = async () => {
        setFailure(null);
        try {
            let signed;
            if (tick) signed = { typedName: typedName.trim() };
            else {
                // the drawn signature goes up to storage first (in the employee's own folder)
                const fileName = `employee/${doc.employeeId}/${Date.now()}documentsign.png`;
                const res = await uploadUrl({ variables: { input: { fileName, objectType: "employee" } } });
                const link = res?.data?.genericFileUploadSignatureUrl?.response;
                const blob = await (await fetch(signature.signature)).blob();
                const put = await fetch(link.url, { method: "PUT", body: blob, headers: { "Content-Type": "image/png" } });
                if (!put.ok) throw new Error("Your signature didn't upload. Please try again.");
                signed = { signatureKey: link.originalFileName || fileName };
            }
            await acknowledge({ variables: { input: { shareId, answers, ...signed } } });
            const message = tick ? "Thanks - your acknowledgement has been recorded." : "Signed - thank you. Your signed copy is in your documents.";
            setDone(message);
            tell({ type: "done", message });
        } catch (err) { setFailure(err.message); }
    };

    const stampValues = {
        employeeName: { text: doc.employeeName },
        signedDate: { text: dayjs().format("DD/MM/YYYY") },
        ...(tick && typedName.trim() ? { employeeSignature: { text: typedName.trim() } } : {}),
    };
    return (
        <>
            <div className="svy-hint" style={{ marginBottom: 12 }}>
                {doc.title}{doc.version > 1 ? ` · version ${doc.version}` : ""}{doc.dueAt ? ` · please confirm by ${dayjs(doc.dueAt).format("D MMMM YYYY")}` : ""}.
                {" "}{questions.length ? "Fill in the highlighted boxes (Start takes you through them), read" : "Read"} it to the end, then {tick ? "confirm" : "sign"} it.
            </div>
            {doc.description && <div className="svy-note">{doc.description}</div>}
            {failure && <div className="svy-note" style={{ background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }}>{failure}</div>}
            <FormOnPdf pdfUrl={doc.formPdfUrl} template={{ sections: doc.form.sections, stamps: doc.form.stamps }} kind="document" mode="fill" side="employee"
                answers={answers} onAnswer={(id, patch) => setAnswers((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }))}
                stampValues={stampValues} signSource={!tick && hasSignBox ? "employeeSignature" : null} signature={signature} onSign={setSignature} signDrawOnly
                onReadToEnd={() => setReadToEnd(true)}
                renderBar={({ answered, total, left, next, nextLabel }) => (
                    <div className="fop-bar">
                        <span className="fop-bar-count">{total ? `${answered} of ${total} filled in` : doc.title}</span>
                        {left > 0 && <button type="button" className="svy-clear" data-next-field onClick={next}>{nextLabel}</button>}
                        <button type="button" className="svy-submit" disabled={sending || !!blocker} onClick={send}>
                            {sending ? "Sending..." : blocker || (tick ? "Confirm" : "Sign and confirm")}
                        </button>
                    </div>
                )} />
            <div className="svy-form" style={{ marginTop: 16 }} data-ack-panel>
                <div className="svy-question-text">{doc.statement}</div>
                {tick ? (
                    <>
                        <label className={`svy-check${confirmed ? " on" : ""}`} data-ack-confirm>
                            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                            <span className="svy-check-box" aria-hidden="true">{confirmed ? "✓" : ""}</span>
                            <span className="svy-check-label">I confirm</span>
                        </label>
                        <input className="svy-input" style={{ marginTop: 10 }} placeholder="Type your full name" value={typedName} maxLength={120} onChange={(e) => setTypedName(e.target.value)} />
                    </>
                ) : hasSignBox ? (
                    <div className="svy-hint">{signature ? "Signed in the signature box on the document." : "You sign in the signature box on the document."}</div>
                ) : (
                    <SignHere drawOnly onChange={(value) => setSignature(value?.signature ? value : null)} />
                )}
            </div>
        </>
    );
};

export const EmbedDocumentPage = () => <Shell><DocumentBody /></Shell>;
